export type SharedServiceConfig = {
  readonly servicePath: string;
  readonly tierSkuId: string;
  readonly laneIds?: readonly string[];
  readonly addOnSkuIds?: readonly string[];
  readonly createdAt: number;
};

type SharedServiceConfigV1 = SharedServiceConfig & {
  readonly v: 1;
  readonly kind: 'service_config';
};

function sanitizeStringArray(values: readonly string[] | undefined): string[] | undefined {
  if (!Array.isArray(values) || !values.length) return undefined;
  const next = values.map((value) => String(value ?? '').trim()).filter(Boolean);
  return next.length ? Array.from(new Set(next)) : undefined;
}

function toBase64Url(raw: string): string {
  const bytes = new TextEncoder().encode(raw);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(encoded: string): string {
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '==='.slice((normalized.length + 3) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function createSharedServiceConfigToken(input: SharedServiceConfig): string {
  const payload: SharedServiceConfigV1 = {
    v: 1,
    kind: 'service_config',
    servicePath: String(input.servicePath ?? '').trim(),
    tierSkuId: String(input.tierSkuId ?? '').trim(),
    laneIds: sanitizeStringArray(input.laneIds),
    addOnSkuIds: sanitizeStringArray(input.addOnSkuIds),
    createdAt: Number.isFinite(input.createdAt) ? input.createdAt : Date.now(),
  };

  if (!payload.servicePath || !payload.tierSkuId) {
    throw new Error('Missing required service config share payload fields.');
  }

  return toBase64Url(JSON.stringify(payload));
}

export function parseSharedServiceConfigToken(token: string): SharedServiceConfig | null {
  try {
    const decoded = fromBase64Url(String(token ?? '').trim());
    const parsed = JSON.parse(decoded) as Partial<SharedServiceConfigV1>;
    if (!parsed || parsed.v !== 1 || parsed.kind !== 'service_config') return null;

    const servicePath = String(parsed.servicePath ?? '').trim();
    const tierSkuId = String(parsed.tierSkuId ?? '').trim();
    if (!servicePath || !tierSkuId) return null;

    return {
      servicePath,
      tierSkuId,
      laneIds: sanitizeStringArray(parsed.laneIds),
      addOnSkuIds: sanitizeStringArray(parsed.addOnSkuIds),
      createdAt: Number.isFinite(Number(parsed.createdAt))
        ? Number(parsed.createdAt)
        : Date.now(),
    };
  } catch {
    return null;
  }
}

export function buildSharedServiceConfigUrl(payload: SharedServiceConfig): string {
  const token = createSharedServiceConfigToken(payload);
  const url = new URL(payload.servicePath, window.location.origin);
  url.searchParams.set('config', token);
  return url.toString();
}
